import React, { useEffect, useState } from "react";
import {
  api,
  CHANNEL_COLORS,
  STATUS_COLORS,
  CHANNELS,
  INQUIRY_TYPES,
  ORDER_STAGES,
  resolveImageUrl,
} from "@/lib/api";
import { INQUIRY } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Send, Sparkles, Phone, MessageCircle, X } from "lucide-react";
import InvoicePanel from "@/components/saheli/InvoicePanel";

function InquiryDetail({ inquiry: initialInquiry, onClose, onUpdated }) {
  const [inquiry, setInquiry] = useState(initialInquiry);
  const [language, setLanguage] = useState("English");
  const [reply, setReply] = useState(initialInquiry?.ai_reply || "");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(initialInquiry?.status || "New Inquiry");
  const [includeMatchImage, setIncludeMatchImage] = useState(true);

  useEffect(() => {
    setInquiry(initialInquiry);
    setReply(initialInquiry?.ai_reply || "");
    setStatus(initialInquiry?.status || "New Inquiry");
  }, [initialInquiry?.id, initialInquiry?.ai_reply, initialInquiry?.status, initialInquiry]);

  const refreshInquiry = async () => {
    if (!initialInquiry?.id) return;
    try {
      const { data } = await api.get(`/inquiries/${initialInquiry.id}`);
      setInquiry(data);
    } catch (err) {
      console.warn('Saheli: request failed', err);
    }
    onUpdated?.();
  };

  if (!inquiry) return null;

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/inquiries/${inquiry.id}/ai-reply`, {
        language,
        product_id: inquiry.auto_match_id || undefined,
      });
      setReply(data.ai_reply || "");
      if (!data.ai_reply || !data.ai_reply.trim()) {
        toast.warning("AI returned an empty draft — please try again");
      } else {
        toast.success("AI reply drafted");
      }
    } catch {
      toast.error("Failed to generate reply");
    } finally {
      setBusy(false);
    }
  };

  const saveReply = async () => {
    try {
      await api.patch(`/inquiries/${inquiry.id}/reply`, { ai_reply: reply });
      toast.success("Draft saved");
      onUpdated?.();
    } catch {
      toast.error("Save failed");
    }
  };

  const markSent = async () => {
    try {
      await api.patch(`/inquiries/${inquiry.id}/reply`, {
        ai_reply: reply,
        replied: true,
      });
      toast.success("Marked as replied");
      onUpdated?.();
      onClose?.();
    } catch {
      toast.error("Failed");
    }
  };

  const sendViaManychat = async () => {
    if (!reply.trim()) {
      toast.error("Nothing to send — write or draft a reply first");
      return;
    }
    setSending(true);
    try {
      await api.post(`/inquiries/${inquiry.id}/send-manychat`, {
        text: reply,
        include_match_image: includeMatchImage && !!inquiry.auto_match_id,
      });
      toast.success(`Sent to ${inquiry.customer_name} via ${inquiry.channel}`);
      onUpdated?.();
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "ManyChat send failed";
      toast.error(String(msg));
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (val) => {
    setStatus(val);
    setInquiry((prev) => (prev ? { ...prev, status: val } : prev));
    try {
      await api.patch(`/inquiries/${inquiry.id}/status`, { status: val });
      toast.success(`Moved to ${val}`);
      onUpdated?.();
    } catch {
      toast.error("Status update failed");
    }
  };

  const isManychat = inquiry.source === "manychat" && inquiry.manychat_subscriber_id;

  return (
    <Dialog open={!!inquiry} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        data-testid={INQUIRY.detailDialog}
        className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-stone-900">
            {inquiry.customer_name}
          </DialogTitle>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <span className={`heritage-badge ${CHANNEL_COLORS[inquiry.channel] || ""}`}>
              {inquiry.channel}
            </span>
            <span className={`heritage-badge ${STATUS_COLORS[inquiry.status] || ""}`}>
              {inquiry.status}
            </span>
            <span className="heritage-badge">{inquiry.inquiry_type}</span>
            {inquiry.source === "manychat" && (
              <span className="heritage-badge bg-terracotta/10 text-terracotta border-terracotta/20">
                ManyChat
              </span>
            )}
            {inquiry.phone && (
              <span className="text-xs text-stone-500 flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {inquiry.phone}
              </span>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="overline mb-2">Customer message</div>
            <div className="text-sm text-stone-700 leading-relaxed bg-stone-50 border border-stone-200 rounded-xl p-3">
              {inquiry.message || <span className="text-stone-400">(no text)</span>}
            </div>
            {inquiry.image_url && (
              <img
                src={inquiry.image_url}
                alt=""
                className="mt-2 rounded-lg border border-stone-200 max-h-48"
              />
            )}
            {inquiry.link && (
              <a
                href={inquiry.link}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-terracotta underline mt-2 inline-block"
              >
                {inquiry.link}
              </a>
            )}
          </div>

          {inquiry.auto_match_id && (
            <div
              data-testid={INQUIRY.autoMatchBadge}
              className="ai-highlight rounded-xl p-3 flex gap-3 items-center"
            >
              {inquiry.auto_match_photo_url && (
                <img
                  src={resolveImageUrl(inquiry.auto_match_photo_url)}
                  alt=""
                  className="w-16 h-20 rounded-lg object-cover border border-stone-200 shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="overline text-terracotta">Saheli found a match</div>
                <div className="font-serif text-lg text-stone-800 truncate">
                  {inquiry.auto_match_name}
                </div>
                <div className="text-xs text-stone-500">
                  {Math.round(inquiry.auto_match_similarity || 0)}% match · ₹
                  {Number(inquiry.auto_match_price || 0).toLocaleString("en-IN")}
                </div>
              </div>
            </div>
          )}

          <div>
            <div className="overline mb-2">Order status</div>
            <Select value={status} onValueChange={changeStatus}>
              <SelectTrigger
                data-testid={INQUIRY.statusSelect}
                className="w-64"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ORDER_STAGES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <InvoicePanel inquiry={inquiry} onUpdated={refreshInquiry} />

          <div className="ai-highlight rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="overline text-terracotta">AI-drafted reply</div>
              <div className="flex items-center gap-2">
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger
                    data-testid={INQUIRY.aiReplyLanguage}
                    className="h-8 w-32 text-xs"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="English">English</SelectItem>
                    <SelectItem value="Hindi">Hindi</SelectItem>
                    <SelectItem value="Hinglish">Hinglish</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={generate}
                  disabled={busy}
                  data-testid={INQUIRY.aiReplyGenerate}
                  className="text-terracotta hover:bg-terracotta/10 h-8"
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1" />
                  {busy ? "Drafting…" : "Draft with AI"}
                </Button>
              </div>
            </div>
            <Textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Click 'Draft with AI' to auto-generate a reply — editable before sending."
              rows={5}
              data-testid={INQUIRY.aiReplyText}
              className="bg-white/70"
            />
            {isManychat && inquiry.auto_match_id && (
              <label className="flex items-center gap-2 mt-2 text-xs text-stone-600 cursor-pointer select-none">
                <Checkbox
                  checked={includeMatchImage}
                  onCheckedChange={setIncludeMatchImage}
                />
                Attach the matched saree image when sending
              </label>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="ghost" onClick={saveReply}>
            Save draft
          </Button>
          {isManychat ? (
            <>
              <Button variant="outline" onClick={markSent}>
                <Send className="w-4 h-4 mr-2" />
                Mark replied
              </Button>
              <Button
                data-testid={INQUIRY.aiReplySendManychat}
                disabled={sending || !reply.trim()}
                className="bg-terracotta hover:bg-terracotta-dark"
                onClick={sendViaManychat}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {sending ? "Sending…" : "Send via ManyChat"}
              </Button>
            </>
          ) : (
            <Button
              data-testid={INQUIRY.aiReplyMarkSent}
              className="bg-terracotta hover:bg-terracotta-dark"
              onClick={markSent}
            >
              <Send className="w-4 h-4 mr-2" />
              Mark as replied
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateInquiryDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    channel: "WhatsApp",
    message: "",
    inquiry_type: "",
  });
  const submit = async () => {
    try {
      const payload = { ...form };
      if (!payload.inquiry_type) delete payload.inquiry_type;
      await api.post("/inquiries", payload);
      toast.success("Inquiry created");
      setOpen(false);
      setForm({
        customer_name: "",
        phone: "",
        channel: "WhatsApp",
        message: "",
        inquiry_type: "",
      });
      onCreated?.();
    } catch {
      toast.error("Failed to create inquiry");
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid={INQUIRY.addButton}
          className="bg-terracotta hover:bg-terracotta-dark rounded-full"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          New Inquiry
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={INQUIRY.createDialog} className="bg-white">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Log a new inquiry</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Input
            data-testid={INQUIRY.createNameInput}
            placeholder="Customer name"
            value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
          />
          <Input
            data-testid={INQUIRY.createPhoneInput}
            placeholder="Phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <Select
            value={form.channel}
            onValueChange={(v) => setForm({ ...form, channel: v })}
          >
            <SelectTrigger data-testid={INQUIRY.createChannel}>
              <SelectValue placeholder="Channel" />
            </SelectTrigger>
            <SelectContent>
              {CHANNELS.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={form.inquiry_type}
            onValueChange={(v) => setForm({ ...form, inquiry_type: v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Inquiry type (or auto)" />
            </SelectTrigger>
            <SelectContent>
              {INQUIRY_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Textarea
          data-testid={INQUIRY.createMessage}
          placeholder="Customer message"
          rows={4}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            data-testid={INQUIRY.createSubmit}
            className="bg-terracotta hover:bg-terracotta-dark"
            onClick={submit}
          >
            Save inquiry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Inquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (channelFilter !== "all") params.channel = channelFilter;
      const { data } = await api.get("/inquiries", { params });
      setInquiries(data || []);
    } catch (err) {
      console.warn('Saheli: request failed', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, channelFilter]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedIds.size === inquiries.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(inquiries.map((i) => i.id)));
  };

  const bulkMarkReplied = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/inquiries/bulk", {
        action: "mark_replied",
        ids: [...selectedIds],
      });
      toast.success(`Marked ${data.modified} as replied`);
      setSelectedIds(new Set());
      load();
    } catch {
      toast.error("Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkSetStatus = async (status) => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/inquiries/bulk", {
        action: "set_status",
        ids: [...selectedIds],
        status,
      });
      toast.success(`Moved ${data.modified} to ${status}`);
      setSelectedIds(new Set());
      load();
    } catch {
      toast.error("Bulk move failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="overline">Unified inbox</div>
          <h1 className="font-serif text-4xl text-stone-900 mt-1">Inquiries</h1>
          <p className="text-stone-500 text-sm mt-2">
            Every message from WhatsApp, Instagram, phone and walk-ins — auto-categorised.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid={INQUIRY.filterStatus} className="w-40 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {ORDER_STAGES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={channelFilter} onValueChange={setChannelFilter}>
            <SelectTrigger data-testid={INQUIRY.filterChannel} className="w-40 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All channels</SelectItem>
              {CHANNELS.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <CreateInquiryDialog onCreated={load} />
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div
          data-testid={INQUIRY.bulkToolbar}
          className="paper-card p-3 flex items-center gap-3 border-terracotta/30 bg-terracotta/5"
        >
          <div className="text-sm text-stone-700 font-medium">
            {selectedIds.size} selected
          </div>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-stone-400 hover:text-stone-700"
            title="Clear"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex-1" />
          <Button
            data-testid={INQUIRY.bulkMarkReplied}
            variant="outline"
            size="sm"
            disabled={bulkBusy}
            onClick={bulkMarkReplied}
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            Mark replied
          </Button>
          <Select
            onValueChange={(v) => v && bulkSetStatus(v)}
            disabled={bulkBusy}
          >
            <SelectTrigger
              data-testid={INQUIRY.bulkSetStatus}
              className="w-44 h-9 bg-white"
            >
              <SelectValue placeholder="Move to stage…" />
            </SelectTrigger>
            <SelectContent>
              {ORDER_STAGES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="paper-card overflow-hidden">
        <table data-testid={INQUIRY.table} className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-stone-200 bg-stone-50/50">
              <th className="px-3 py-3 w-8">
                <Checkbox
                  data-testid={INQUIRY.bulkSelectAll}
                  checked={inquiries.length > 0 && selectedIds.size === inquiries.length}
                  onCheckedChange={toggleAll}
                />
              </th>
              {["Customer", "Channel", "Type", "Message", "Status", "Received"].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-[11px] uppercase tracking-wider text-stone-500 font-medium"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && inquiries.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-stone-400">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && inquiries.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center">
                  <div className="text-stone-500 text-sm mb-2">No inquiries yet</div>
                  <div className="text-xs text-stone-400">
                    Log one manually, or wire up your ManyChat webhook — new messages
                    will flow in automatically.
                  </div>
                </td>
              </tr>
            )}
            {inquiries.map((i) => (
              <tr
                key={i.id}
                data-testid={INQUIRY.row(i.id)}
                className="border-b border-stone-100 hover:bg-stone-50 transition-colors"
              >
                <td
                  className="px-3 py-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    data-testid={INQUIRY.rowCheckbox(i.id)}
                    checked={selectedIds.has(i.id)}
                    onCheckedChange={() => toggleSelect(i.id)}
                  />
                </td>
                <td className="px-4 py-3 cursor-pointer" onClick={() => setSelected(i)}>
                  <div className="text-stone-800 font-medium">{i.customer_name}</div>
                  <div className="text-xs text-stone-500">{i.phone || "—"}</div>
                </td>
                <td className="px-4 py-3 cursor-pointer" onClick={() => setSelected(i)}>
                  <span className={`heritage-badge ${CHANNEL_COLORS[i.channel] || ""}`}>
                    {i.channel}
                  </span>
                </td>
                <td className="px-4 py-3 cursor-pointer" onClick={() => setSelected(i)}>
                  <span className="heritage-badge">{i.inquiry_type}</span>
                </td>
                <td
                  className="px-4 py-3 max-w-sm cursor-pointer"
                  onClick={() => setSelected(i)}
                >
                  <div className="text-stone-700 truncate">{i.message || "—"}</div>
                  {i.auto_match_name && (
                    <div className="text-[11px] text-terracotta mt-0.5 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Auto-matched: {i.auto_match_name} (
                      {Math.round(i.auto_match_similarity || 0)}%)
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 cursor-pointer" onClick={() => setSelected(i)}>
                  <span className={`heritage-badge ${STATUS_COLORS[i.status] || ""}`}>
                    {i.status}
                  </span>
                </td>
                <td
                  className="px-4 py-3 text-xs text-stone-500 cursor-pointer"
                  onClick={() => setSelected(i)}
                >
                  {new Date(i.created_at).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <InquiryDetail
        inquiry={selected}
        onClose={() => setSelected(null)}
        onUpdated={load}
      />
    </div>
  );
}
